import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { createEntitySlice, EntityState, serializeAxiosError } from 'src/app/reducers/utils/reducer.utils';
import { IElectricSgmAlarmLog, defaultValue } from 'src/shared/model/electric-sgm-alarm-log.model';
import { cleanEntity } from 'src/shared/utils/entity-utils';

const initialState: EntityState<IElectricSgmAlarmLog> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/electric-sgm-alarm-logs';

// Actions
// electric-sgm-alarm-logs-count-by-devid-and-status
export const getConvElectricAlarmLogsByStatus = createAsyncThunk('convElectricAlarmLog/fetch_entity_list', async (props: { devId: string; status: string }) => {
  const requestUrl = `${apiUrl}-by-devid-and-status?devId=${props.devId}&status=${props.status}`;
  return axios.get<IElectricSgmAlarmLog[]>(requestUrl);
});

export const getEntities = createAsyncThunk('convElectricAlarmLog/fetch_entity_list', async () => {
  const requestUrl = `${apiUrl}?cacheBuster=${new Date().getTime()}`;
  return axios.get<IElectricSgmAlarmLog[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'convElectricAlarmLog/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IElectricSgmAlarmLog>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'convElectricAlarmLog/create_entity',
  async (entity: IElectricSgmAlarmLog, thunkAPI) => {
    const result = await axios.post<IElectricSgmAlarmLog>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities());
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'convElectricAlarmLog/update_entity',
  async (entity: IElectricSgmAlarmLog, thunkAPI) => {
    const result = await axios.put<IElectricSgmAlarmLog>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'convElectricAlarmLog/partial_update_entity',
  async (entity: IElectricSgmAlarmLog, thunkAPI) => {
    const result = await axios.patch<IElectricSgmAlarmLog>(`${apiUrl}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities());
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'convElectricAlarmLog/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IElectricSgmAlarmLog>(requestUrl);
    thunkAPI.dispatch(getEntities());
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const countConvElectricAlarmLogsByDevIdAndStatus = createAsyncThunk('convElectricAlarmLog/count_entity_list', async (props: { devId: string; status: string }) => {
  const requestUrl = `${apiUrl}-count-by-devid-and-status?devId=${props.devId}&status=${props.status}`;
  return axios.get<number>(requestUrl);
});

// slice

export const ASlice = createEntitySlice({
  name: 'convElectricAlarmLog',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(countConvElectricAlarmLogsByDevIdAndStatus.fulfilled, (state, action) => {
        state.totalItems = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities, getConvElectricAlarmLogsByStatus), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getConvElectricAlarmLogsByStatus, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ASlice.actions;

// Reducer
export default ASlice.reducer;
